import { addExceptionNotification, addSuccess } from '@mse-reg/app/modules/notification.slice';
import { createAppThunk } from '@mse-reg/app/store';
import { CommentDto, ExamRegistrationDto, ExamRegistrationState, IExamRegistrationClient, IRegistrationEditClient, PreviousExamAttemptDto, RegistrationStateChangeDto } from '@mse-reg/clients';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { saveAs } from 'file-saver';

export interface ExamRegistrationsState {
  registrations: ExamRegistrationDto[];
  loading: boolean;
  exportRegistrations: ExamRegistrationDto[];
  exportLoading: boolean;
}

const initialState: ExamRegistrationsState = {
  registrations: [],
  loading: false,
  exportRegistrations: [],
  exportLoading: false,
};

const fetchExamRegistrationsAsync = createAppThunk(
  'examRegistrations/FETCH',
  async (
    args: { semesterId: number; uasId?: number },
    deps: { examRegistrationClient: IExamRegistrationClient },
  ) => {
    const result = await deps.examRegistrationClient.getExamRegistrationsForSemester(args.semesterId, args.uasId ?? null);
    return result;
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const fetchExportRegistrationsAsync = createAppThunk(
  'examRegistrations/FETCH_EXPORT',
  async (
    args: { semesterId: number; uasId?: number },
    deps: { examRegistrationClient: IExamRegistrationClient },
  ) => {
    return await deps.examRegistrationClient.getExamRegistrationsForSemester(args.semesterId, args.uasId ?? null);
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const fetchExportRegistrationsBulkAsync = createAppThunk(
  'examRegistrations/FETCH_EXPORT_BULK',
  async (
    args: { semesterIds: number[]; uasIds?: number[] },
    deps: { examRegistrationClient: IExamRegistrationClient },
  ) => {
    if (!args.semesterIds?.length) {
      return [] as ExamRegistrationDto[];
    }

    const useAllUas = !args.uasIds || args.uasIds.length === 0;
    const requests = useAllUas
      ? args.semesterIds.map((semesterId) => deps.examRegistrationClient.getExamRegistrationsForSemester(semesterId, null))
      : args.semesterIds.flatMap((semesterId) =>
        args.uasIds.map((uasId) => deps.examRegistrationClient.getExamRegistrationsForSemester(semesterId, uasId)),
      );

    const results = await Promise.all(requests);
    const merged = results.flat();
    const deduped = new Map<string, ExamRegistrationDto>();

    merged.forEach((r) => {
      const key = r.examRegistrationId > 0 ? `e-${r.examRegistrationId}` : `m-${r.moduleRegistrationId}`;
      if (!deduped.has(key)) {
        deduped.set(key, r);
      }
    });

    return [...deduped.values()];
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const addResitAsync = createAppThunk(
  'examRegistrations/ADD_Resit',
  async (
    moduleRegistrationId: number,
    deps: { examRegistrationClient: IExamRegistrationClient },
    dispatch,
  ) => {
    try {
      const result = await deps.examRegistrationClient.addResitExamRegistration(moduleRegistrationId);
      dispatch(addSuccess('Registered for resit exam.'));
      return result;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'Failed to register for resit exam.'));
      return undefined;
    }
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const withdrawAsync = createAppThunk(
  'examRegistrations/WITHDRAW',
  async (
    examRegistrationId: number,
    deps: { examRegistrationClient: IExamRegistrationClient },
    dispatch,
  ) => {
    try {
      await deps.examRegistrationClient.withdrawExamRegistration(examRegistrationId);
      dispatch(addSuccess('Marked as absent.'));
      return examRegistrationId;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'Failed to mark exam registration as absent.'));
      return undefined;
    }
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const confirmAsync = createAppThunk(
  'examRegistrations/CONFIRM',
  async (
    examRegistrationId: number,
    deps: { examRegistrationClient: IExamRegistrationClient },
    dispatch,
  ) => {
    try {
      await deps.examRegistrationClient.confirmExamRegistration(examRegistrationId);
      dispatch(addSuccess('Exam registration confirmed.'));
      return examRegistrationId;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'Failed to confirm exam registration.'));
      return undefined;
    }
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const addCommentAsync = createAppThunk(
  'examRegistrations/ADD_COMMENT',
  async (comment: CommentDto, deps: { registrationEditClient: IRegistrationEditClient }, dispatch) => {
    try {
      const id = await deps.registrationEditClient.addComment(comment);
      dispatch(addSuccess('Successfully added comment.'));
      return { ...comment, id } as CommentDto;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while saving comment.'));
    }
    return undefined;
  },
  (r) => ({ registrationEditClient: r.resolve('IRegistrationEditClient') as IRegistrationEditClient }),
);

const removeCommentAsync = createAppThunk(
  'examRegistrations/REMOVE_COMMENT',
  async (comment: CommentDto, deps: { registrationEditClient: IRegistrationEditClient }, dispatch) => {
    try {
      await deps.registrationEditClient.deleteComment(comment.id);
      dispatch(addSuccess('Successfully deleted comment.'));
    } catch (e) {
      dispatch(addExceptionNotification(e, 'An unknown error occurred while deleting comment.'));
    }
  },
  (r) => ({ registrationEditClient: r.resolve('IRegistrationEditClient') as IRegistrationEditClient }),
);

const getModuleRegistrationAsync = createAppThunk(
  'examRegistrations/GET_MODULE_REGISTRATION',
  async (moduleRegistrationId: number, deps: { registrationEditClient: IRegistrationEditClient }) => {
    return await deps.registrationEditClient.getModuleRegistration(moduleRegistrationId);
  },
  (r) => ({ registrationEditClient: r.resolve('IRegistrationEditClient') as IRegistrationEditClient }),
);

const getHistoryAsync = createAppThunk(
  'examRegistrations/GET_HISTORY',
  async (moduleRegistrationId: number, deps: { registrationEditClient: IRegistrationEditClient }): Promise<RegistrationStateChangeDto[]> => {
    return await deps.registrationEditClient.getHistory(moduleRegistrationId);
  },
  (r) => ({ registrationEditClient: r.resolve('IRegistrationEditClient') as IRegistrationEditClient }),
);

const getPreviousAttemptsAsync = createAppThunk(
  'examRegistrations/GET_PREVIOUS_ATTEMPTS',
  async (moduleRegistrationId: number, deps: { examRegistrationClient: IExamRegistrationClient }): Promise<PreviousExamAttemptDto[]> => {
    return await deps.examRegistrationClient.getPreviousExamAttempts(moduleRegistrationId);
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const deleteResitAsync = createAppThunk(
  'examRegistrations/DELETE_RESIT',
  async (
    examRegistrationId: number,
    deps: { examRegistrationClient: IExamRegistrationClient },
    dispatch,
  ) => {
    try {
      await deps.examRegistrationClient.deleteResitExamRegistration(examRegistrationId);
      dispatch(addSuccess('Resit exam registration deleted.'));
      return examRegistrationId;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'Failed to delete resit exam registration.'));
      return undefined;
    }
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const setOpeningDateAsync = createAppThunk(
  'examRegistrations/SET_OPENING_DATE',
  async (
    args: { examRegistrationId: number; openingDate: string | null },
    deps: { examRegistrationClient: IExamRegistrationClient },
    dispatch,
  ) => {
    try {
      await deps.examRegistrationClient.setOpeningDate(args.examRegistrationId, { openingDate: args.openingDate ?? undefined });
      return args;
    } catch (e) {
      dispatch(addExceptionNotification(e, 'Failed to set opening date.'));
      return undefined;
    }
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const exportExamsAsync = createAppThunk(
  'examRegistrations/EXPORT',
  async (
    args: { semesterId: number; uasId: number; type: 'regular' | 'resit' | 'all'; moduleId?: number },
    deps: { examRegistrationClient: IExamRegistrationClient },
    dispatch,
  ) => {
    try {
      const result = await deps.examRegistrationClient.exportExams(args.semesterId, args.uasId, args.type, args.moduleId ?? null);
      saveAs(result.data, result.fileName);
    } catch (e) {
      dispatch(addExceptionNotification(e, 'Failed to export exam registrations.'));
    }
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const exportAllExamsAsync = createAppThunk(
  'examRegistrations/EXPORT_ALL',
  async (
    args: { semesterId: number; type: 'regular' | 'resit' | 'all'; moduleId?: number },
    deps: { examRegistrationClient: IExamRegistrationClient },
    dispatch,
  ) => {
    try {
      const result = await deps.examRegistrationClient.exportAllExams(args.semesterId, args.type, args.moduleId ?? null);
      saveAs(result.data, result.fileName);
    } catch (e) {
      dispatch(addExceptionNotification(e, 'Failed to export exam registrations.'));
    }
  },
  (r) => ({ examRegistrationClient: r.resolve('IExamRegistrationClient') as IExamRegistrationClient }),
);

const examRegistrationsSlice = createSlice({
  name: 'examRegistrations',
  initialState,
  reducers: {},
  extraReducers(builder) {
    builder
      .addCase(fetchExamRegistrationsAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchExamRegistrationsAsync.fulfilled, (state, action: PayloadAction<ExamRegistrationDto[]>) => {
        state.loading = false;
        state.registrations = action.payload ?? [];
      })
      .addCase(fetchExamRegistrationsAsync.rejected, (state) => {
        state.loading = false;
      })
      .addCase(addResitAsync.fulfilled, (state, action: PayloadAction<ExamRegistrationDto | undefined>) => {
        if (action.payload) {
          state.registrations.push(action.payload);
        }
      })
      .addCase(withdrawAsync.fulfilled, (state, action: PayloadAction<number | undefined>) => {
        if (action.payload !== undefined) {
          const reg = state.registrations.find((r) => r.examRegistrationId === action.payload);
          if (reg) reg.state = ExamRegistrationState.ABSENT_EXCUSED;
        }
      })
      .addCase(confirmAsync.fulfilled, (state, action: PayloadAction<number | undefined>) => {
        if (action.payload !== undefined) {
          const reg = state.registrations.find((r) => r.examRegistrationId === action.payload);
          if (reg) reg.state = ExamRegistrationState.CONFIRMED;
        }
      })
      .addCase(deleteResitAsync.fulfilled, (state, action: PayloadAction<number | undefined>) => {
        if (action.payload !== undefined) {
          state.registrations = state.registrations.filter((r) => r.examRegistrationId !== action.payload);
        }
      })
      .addCase(setOpeningDateAsync.fulfilled, (state, action) => {
        if (action.payload) {
          const reg = state.registrations.find((r) => r.examRegistrationId === action.payload.examRegistrationId);
          if (reg) reg.openingDate = action.payload.openingDate ?? undefined;
        }
      })
      .addCase(fetchExportRegistrationsAsync.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(fetchExportRegistrationsAsync.fulfilled, (state, action: PayloadAction<ExamRegistrationDto[]>) => {
        state.exportLoading = false;
        state.exportRegistrations = action.payload ?? [];
      })
      .addCase(fetchExportRegistrationsAsync.rejected, (state) => {
        state.exportLoading = false;
      })
      .addCase(fetchExportRegistrationsBulkAsync.pending, (state) => {
        state.exportLoading = true;
      })
      .addCase(fetchExportRegistrationsBulkAsync.fulfilled, (state, action: PayloadAction<ExamRegistrationDto[]>) => {
        state.exportLoading = false;
        state.exportRegistrations = action.payload ?? [];
      })
      .addCase(fetchExportRegistrationsBulkAsync.rejected, (state) => {
        state.exportLoading = false;
      });
  },
});

export const examRegistrationsModule = {
  fetchAsync: fetchExamRegistrationsAsync,
  fetchExportAsync: fetchExportRegistrationsAsync,
  fetchExportBulkAsync: fetchExportRegistrationsBulkAsync,
  addResitAsync,
  withdrawAsync,
  confirmAsync,
  deleteResitAsync,
  setOpeningDateAsync,
  addCommentAsync,
  removeCommentAsync,
  getModuleRegistrationAsync,
  getHistoryAsync,
  getPreviousAttemptsAsync,
  exportExamsAsync,
  exportAllExamsAsync,
  reducer: examRegistrationsSlice.reducer,
};
